import { useCallback, useEffect, useRef, useState } from "react";
import type { ChangeItem, ChangePage, ChangeReceipt } from "../shared/changes";
export type ChangesView = "new" | "session" | "recent";
export function useChanges(board: string, user: string, connected: boolean) {
  const [open, setOpen] = useState(false),
    [view, setView] = useState<ChangesView>("new");
  const [page, setPage] = useState<ChangePage | null>(null),
    [items, setItems] = useState<ChangeItem[]>([]);
  const [error, setError] = useState(""),
    [loading, setLoading] = useState(false),
    [newer, setNewer] = useState(false);
  const state = useRef({ open, view, connected });
  state.current = { open, view, connected };
  const pageRef = useRef(page);
  pageRef.current = page;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const sessionHead = useRef<number | undefined>(undefined),
    displayHead = useRef(0),
    request = useRef(0);
  const mounted = useRef(true),
    denied = useRef(false),
    pending = useRef(new Map<number, Set<number>>());
  const sending = useRef(false);
  const interactions = useRef(new Map<string, string>());
  const base = `/api/rooms/${encodeURIComponent(board)}/changes`;
  const call = useCallback(
    async <T>(url: string, body?: unknown): Promise<T> => {
      const res = await fetch(url, {
        ...(body
          ? {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            }
          : {}),
        signal: AbortSignal.timeout(10000),
      });
      if (res.status === 401 || res.status === 403 || res.status === 404) {
        denied.current = true;
        request.current++;
        pending.current.clear();
        interactions.current.clear();
        if (mounted.current) {
          setPage(null);
          setItems([]);
          setOpen(false);
          setError("Project access is no longer available.");
        }
        throw new Error("Project access is no longer available.");
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Changes unavailable");
      return data;
    },
    [],
  );
  const refresh = useCallback(
    async (snapshot = false, append = false) => {
      if (denied.current || !state.current.connected) return;
      snapshot ||= sessionHead.current === undefined;
      const id = ++request.current,
        currentView = state.current.view;
      const params = new URLSearchParams();
      if (currentView !== "new") params.set("recent", "1");
      if (currentView === "session" && sessionHead.current !== undefined)
        params.set("since", String(sessionHead.current));
      if (append && pageRef.current?.cursor) {
        params.set("before", String(pageRef.current.cursor));
        params.set("head", String(displayHead.current));
      }
      if (snapshot || append) setLoading(true);
      try {
        const next = await call<ChangePage>(
          `${base}?${params}`,
          snapshot ? { action: "snapshot" } : undefined,
        );
        if (!mounted.current || id !== request.current) return;
        const epochChanged =
          pageRef.current && pageRef.current.epoch !== next.epoch;
        if (epochChanged) {
          pending.current.clear();
          sessionHead.current = next.head;
          setItems([]);
        }
        sessionHead.current ??= next.head;
        if (
          snapshot ||
          append ||
          !state.current.open ||
          epochChanged ||
          !pageRef.current
        ) {
          setItems((old) =>
            append
              ? [
                  ...old,
                  ...next.items.filter(
                    (i) => !old.some((o) => o.seq === i.seq),
                  ),
                ]
              : next.items,
          );
          displayHead.current = next.head;
          setNewer(false);
          setPage(next);
        } else {
          setNewer(next.head > displayHead.current);
          setPage((old) =>
            old
              ? { ...old, unread: next.unread, truncated: next.truncated }
              : next,
          );
        }
        setError("");
      } catch (e) {
        if (mounted.current && id === request.current)
          setError((e as Error).message);
      } finally {
        if (mounted.current && id === request.current) setLoading(false);
      }
    },
    [base, call],
  );
  const flush = useCallback(async () => {
    if (
      sending.current ||
      (!pending.current.size && !interactions.current.size) ||
      denied.current ||
      !state.current.connected ||
      !pageRef.current
    )
      return;
    sending.current = true;
    const epoch = pageRef.current.epoch;
    const records = [...interactions.current]
      .slice(0, 50)
      .map(([id, signature]) => ({ id, signature }));
    const receipts: ChangeReceipt[] = [...pending.current]
      .slice(0, 50)
      .map(([seq, targets]) => ({ seq, targets: [...targets] }));
    try {
      if (records.length) await call(base, { action: "interact", records });
      if (receipts.length)
        await call(base, { action: "seen", epoch, receipts });
      for (const r of records)
        if (interactions.current.get(r.id) === r.signature)
          interactions.current.delete(r.id);
      if (!mounted.current || pageRef.current?.epoch !== epoch) return;
      let newlySeen = 0;
      const updated = itemsRef.current.map((item) => {
        const receipt = receipts.find((r) => r.seq === item.seq);
        if (!receipt) return item;
        const seen = [...new Set([...item.seen, ...receipt.targets])];
        if (
          item.actor !== user &&
          item.seen.length < item.targets.length &&
          seen.length === item.targets.length
        )
          newlySeen++;
        return { ...item, seen };
      });
      itemsRef.current = updated;
      setItems(updated);
      setPage((p) =>
        p ? { ...p, unread: Math.max(0, p.unread - newlySeen) } : p,
      );
      for (const r of receipts) {
        const values = pending.current.get(r.seq);
        r.targets.forEach((i) => values?.delete(i));
        if (!values?.size) pending.current.delete(r.seq);
      }
      setError("");
    } catch {
      if (mounted.current && !denied.current)
        setError("Seen status could not be saved. Retrying…");
    } finally {
      sending.current = false;
    }
  }, [base, call, user]);
  const interact = useCallback(
    (records: { id: string; signature: string }[]) => {
      for (const r of records) interactions.current.set(r.id, r.signature);
      if (interactions.current.size > 100)
        interactions.current = new Map([...interactions.current].slice(-100));
    },
    [],
  );
  const mark = useCallback(
    (seq: number, targets: number[]) => {
      const item = itemsRef.current.find((i) => i.seq === seq);
      if (!item || item.actor === user) return;
      const values = pending.current.get(seq) ?? new Set<number>();
      for (const i of targets) if (!item.seen.includes(i)) values.add(i);
      if (values.size) pending.current.set(seq, values);
    },
    [user],
  );
  useEffect(() => {
    mounted.current = true;
    void refresh(true);
    const poll = setInterval(() => {
      if (!document.hidden) void refresh();
    }, 5000);
    const save = setInterval(() => void flush(), 600);
    const visible = () => {
      if (!document.hidden) {
        void refresh();
        void flush();
      }
    };
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("planroom:changes-seen", visible);
    return () => {
      mounted.current = false;
      request.current++;
      clearInterval(poll);
      clearInterval(save);
      document.removeEventListener("visibilitychange", visible);
      window.removeEventListener("planroom:changes-seen", visible);
    };
  }, [refresh, flush]);
  useEffect(() => {
    if (connected) void refresh();
  }, [connected, refresh]);
  const show = () => {
    setOpen(true);
    state.current.open = true;
    void refresh(true);
  };
  const changeView = (v: ChangesView) => {
    setView(v);
    state.current.view = v;
    void refresh(true);
  };
  return {
    open,
    setOpen,
    show,
    view,
    changeView,
    page,
    items,
    error,
    loading,
    newer,
    refresh,
    mark,
    interact,
  };
}
